import { useEffect, useState } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { useLocation } from "react-router-dom";

import Header from "@/components/header/Header";
import CredentialsForm from "@/components/Form/Form";
import CredentialsOtpInput from "@/components/otp-input/OtpInput";

import { LoyaltyCardOtpOptionsResponse } from "@/models/continente-credentials/loyaltycard/LoyaltyCardOtpOptionsResponse";
import { FormError } from "@/models/continente-credentials/common/FormError";

import { cardAssociationService } from "@/services/continente-credentials/mobile/CardAssociationService";

import { useTranslation } from "react-i18next";
import ErrorMessage from "@/components/error-message/error-message";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";
import { EmailAssociationOtpValidation, PhoneNumberAssociationOtpValidation } from "@/models/shared/OtpValidationRequest";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

type Form = (PhoneNumberAssociationOtpValidation | EmailAssociationOtpValidation) & FormError;

export default function LoyaltyCardOtpValidation() {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation<LoyaltyCardOtpOptionsResponse>();
  const { handleResponse } = useApiResponseHandler();

  const userData = location.state || {};
  const nextStep = userData.nextStep || "";

  const properties = userData.properties;
  const { contact } = properties;

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleOTPChange = (value: string) => {
    clearErrors();
    setValue("code", value.trim());
  };

  const onSubmit: SubmitHandler<PhoneNumberAssociationOtpValidation | EmailAssociationOtpValidation> = async (data) => {
    setLoading(true);

    const { data: response, error } = await tryCatch(cardAssociationService.verifyOtp(
      data.code, nextStep
    ));

    handleResponse(
      response,
      {
        setError
      },
      error
    );
    setLoading(false);
  };

  useEffect(() => {
    const otpValidator = (register: UseFormRegister<Form>) =>
      register("code", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]*$/,
          message: t("otp_validation.code_must_be_numeric"),
        },
      });
    otpValidator(register);
  }, [register, t]);

  const headerMessage =
    (contact.type === "MobilePhone"
      ? t("card_association_otp_validation.header_message_phone")
      : t("card_association_otp_validation.header_message_email")) + contact.contact;

  return (
    <CredentialsForm
      id="VerifyOtp"
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t("card_association_otp_validation.header_title")}
        subtitle={headerMessage}
        showLoading={loading}
        previousStepBackXTimes={1}
      />
      <CredentialsOtpInput
        isError={errors.code ? true : false}
        errorMsg={errors.code ? errors.code.message : ""}
        onChange={handleOTPChange}
        isLoyalty
        onResendClick={setLoading}
        isEmail={contact.type === "Email"}
      />
      <ErrorMessage error={errors.submitError} isHtml={true} />
      <ButtonContainer>
        <Button
          type="submit">
          {t("otp_validation.button_next_message")}
        </Button>
        <NeedHelpButton />
      </ButtonContainer>
    </CredentialsForm>
  );
}
